import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHandHoldingDollar, faTriangleExclamation, faWallet, faMoneyBillTransfer,
  faCircleInfo, faClockRotateLeft, faArrowDown,
} from '@fortawesome/free-solid-svg-icons';
import { UiPageHeader, UiButton, UiInput, FIELD_INPUT_CLASS, FIELD_LABEL_CLASS } from '../../components/ui';
import MerchantMultiSelect from '../../components/shared/MerchantMultiSelect';
import { swalConfirm } from '../../components/shared/swalConfirm';

interface WalletLite {
  id: string;
  balance: number;
  payout_balance: number;
}
interface MerchantRow {
  id: string;
  name: string;
  wallet?: WalletLite | null;
}
interface HistoryRow {
  id: string;
  amount: number;
  from_main: number;
  from_payout: number;
  to_main_negative: number;
  balance_before: number;
  balance_after: number;
  payout_balance_before: number;
  payout_balance_after: number;
  note: string;
  admin_name: string;
  admin_type: string;
  created_at: string;
}
interface ReduceResult {
  amount: number;
  from_main: number;
  from_payout: number;
  to_main_negative: number;
  balance: number;
  payout_balance: number;
}

const fmt = (n: any) =>
  `฿${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ReduceCreditPage: React.FC = () => {
  // ⭐ สิทธิ์: เฉพาะ root admin (admin_users) หรือ super_admin employee
  const adminUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('admin_user') || '{}'); } catch { return {}; }
  }, []);
  const isSuperAdmin =
    (adminUser.type === 'admin_employee' && adminUser.role === 'super_admin') || !adminUser.type;

  const [merchants, setMerchants] = useState<MerchantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<ReduceResult | null>(null);

  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const selected = useMemo(() => merchants.find((m) => m.id === selectedId) || null, [merchants, selectedId]);
  const curBalance = Number(selected?.wallet?.balance || 0);
  const curPayout = Number(selected?.wallet?.payout_balance || 0);

  const loadMerchants = useCallback(async () => {
    try {
      setLoading(true);
      const res = await merchantApi.get('/merchants');
      setMerchants(res.data.data || []);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'โหลดข้อมูล Merchant ไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async (merchantId: string) => {
    try {
      setHistoryLoading(true);
      const res = await merchantApi.get(`/admin/merchants/${merchantId}/reduce-credit/history?limit=20`);
      if (res.data.success) setHistory(res.data.data || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { if (isSuperAdmin) loadMerchants(); }, [isSuperAdmin, loadMerchants]);

  useEffect(() => {
    setLastResult(null);
    if (selectedId) loadHistory(selectedId);
    else setHistory([]);
  }, [selectedId, loadHistory]);

  // preview การแตกยอดฝั่ง client (payout ใช้ยอดรวม — ยอดจอง P2P ถูกเคารพจริงที่ server)
  const preview = useMemo(() => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return null;
    let remaining = amt;
    const fromMain = Math.min(remaining, Math.max(0, curBalance));
    remaining -= fromMain;
    const fromPayout = Math.min(remaining, Math.max(0, curPayout));
    remaining -= fromPayout;
    const toMainNegative = remaining;
    return {
      fromMain,
      fromPayout,
      toMainNegative,
      newBalance: curBalance - fromMain - toMainNegative,
      newPayout: curPayout - fromPayout,
    };
  }, [amount, curBalance, curPayout]);

  const handleSubmit = async () => {
    if (!selected) { toast.error('กรุณาเลือก Merchant'); return; }
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error('กรุณาระบุจำนวนเงินที่ถูกต้อง'); return; }
    if (note.trim().length < 3) { toast.error('กรุณาระบุหมายเหตุ (อย่างน้อย 3 ตัวอักษร)'); return; }

    const ok = await swalConfirm({
      title: 'ยืนยันการลดเครดิต?',
      html: `ลดเครดิต <b>${selected.name}</b> จำนวน <b>${fmt(amt)}</b><br/><span style="color:#ef4444">การลดเครดิตอาจทำให้ยอด Main Wallet ติดลบได้</span>`,
      icon: 'warning',
      confirmText: 'ยืนยันลดเครดิต',
      confirmColor: '#ef4444',
    });
    if (!ok) return;

    try {
      setSubmitting(true);
      const res = await merchantApi.post(`/admin/merchants/${selected.id}/reduce-credit`, {
        amount: amt,
        note: note.trim(),
      });
      if (res.data.success) {
        setLastResult(res.data.data);
        toast.success(res.data.message || 'ลดเครดิตสำเร็จ');
        setAmount('');
        setNote('');
        await loadMerchants();
        await loadHistory(selected.id);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ลดเครดิตไม่สำเร็จ');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <>
        <UiPageHeader icon={faHandHoldingDollar} title="ลดเครดิต Merchant" />
        <div className="mt-6 rounded-2xl border border-red-100 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 p-6 text-center">
          <FontAwesomeIcon icon={faTriangleExclamation} className="text-red-500 text-2xl mb-2" />
          <p className="text-sm text-red-600 dark:text-red-400 font-medium">
            เฉพาะ Super Admin เท่านั้นที่สามารถเข้าถึงหน้านี้ได้
          </p>
        </div>
      </>
    );
  }

  const balanceColor = (n: number) =>
    n < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-slate-100';

  return (
    <>
      <UiPageHeader
        icon={faHandHoldingDollar}
        title="ลดเครดิต Merchant"
        subtitle="บังคับหักเครดิต — หัก Main Wallet ก่อน → Payout Wallet → ส่วนที่เหลือดัน Main ให้ติดลบ"
      />

      <p className="mb-4 -mt-2 text-xs text-gray-400 dark:text-slate-500">
        <FontAwesomeIcon icon={faCircleInfo} className="mr-1" />
        ระบบจะหักจาก Main Wallet ก่อน ถ้าไม่พอจึงหักจาก Payout Wallet (เคารพยอดที่ถูกจองไว้จ่ายใบ P2P liquidity)
        และส่วนที่ยังไม่พอจะทำให้ Main Wallet <strong>ติดลบ</strong> ทุกรายการต้องระบุหมายเหตุและถูกบันทึกไว้ตรวจสอบได้
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ── ฟอร์มลดเครดิต ── */}
        <div className="rounded-2xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-4">
          <div>
            <label className={FIELD_LABEL_CLASS}>เลือก Merchant <span className="text-red-500">*</span></label>
            <MerchantMultiSelect
              merchants={merchants}
              value={selectedId}
              onChange={setSelectedId}
              variant="light"
              placeholder={loading ? 'กำลังโหลด...' : 'เลือกร้านค้า'}
            />
          </div>

          {selected && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-gray-50 dark:bg-slate-700/50 p-3">
                <p className="text-[11px] text-gray-500 dark:text-slate-400 mb-0.5">
                  <FontAwesomeIcon icon={faWallet} className="mr-1" />Main Wallet
                </p>
                <p className={`font-bold text-base tabular-nums ${balanceColor(curBalance)}`}>{fmt(curBalance)}</p>
              </div>
              <div className="rounded-xl bg-gray-50 dark:bg-slate-700/50 p-3">
                <p className="text-[11px] text-gray-500 dark:text-slate-400 mb-0.5">
                  <FontAwesomeIcon icon={faMoneyBillTransfer} className="mr-1" />Payout Wallet
                </p>
                <p className={`font-bold text-base tabular-nums ${balanceColor(curPayout)}`}>{fmt(curPayout)}</p>
              </div>
            </div>
          )}

          <UiInput
            label="จำนวนที่ลด"
            required
            prefix="฿"
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            disabled={!selected}
          />

          <div>
            <label className={FIELD_LABEL_CLASS}>หมายเหตุ (เหตุผลที่ลด) <span className="text-red-500">*</span></label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={500}
              disabled={!selected}
              placeholder="ระบุเหตุผลของการลดเครดิต เช่น ปรับยอดผิดพลาด / เรียกคืนเครดิต..."
              className={`${FIELD_INPUT_CLASS} resize-none`}
            />
            <p className="mt-1 text-[11px] text-gray-400 dark:text-slate-500">{note.trim().length}/500</p>
          </div>

          {/* preview การแตกยอด */}
          {selected && preview && (
            <div className="rounded-xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs space-y-1">
              <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">
                <FontAwesomeIcon icon={faArrowDown} className="mr-1" />ประมาณการแตกยอด
              </p>
              <div className="flex justify-between"><span className="text-gray-600 dark:text-slate-300">หักจาก Main Wallet</span><span className="tabular-nums font-medium">{fmt(preview.fromMain)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600 dark:text-slate-300">หักจาก Payout Wallet</span><span className="tabular-nums font-medium">{fmt(preview.fromPayout)}</span></div>
              {preview.toMainNegative > 0 && (
                <div className="flex justify-between text-red-600 dark:text-red-400"><span>ดัน Main ติดลบ</span><span className="tabular-nums font-medium">{fmt(preview.toMainNegative)}</span></div>
              )}
              <div className="border-t border-amber-200 dark:border-amber-900/40 mt-1 pt-1 flex justify-between">
                <span className="text-gray-600 dark:text-slate-300">Main คงเหลือ (ประมาณ)</span>
                <span className={`tabular-nums font-bold ${balanceColor(preview.newBalance)}`}>{fmt(preview.newBalance)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-slate-300">Payout คงเหลือ (ประมาณ)</span>
                <span className={`tabular-nums font-bold ${balanceColor(preview.newPayout)}`}>{fmt(preview.newPayout)}</span>
              </div>
              <p className="text-[10px] text-gray-400 dark:text-slate-500 pt-0.5">* ตัวเลขจริงอาจต่างจากนี้ หาก Payout มียอดจอง P2P — ระบบจะเคารพยอดจองและดัน Main ติดลบแทน</p>
            </div>
          )}

          <UiButton
            variant="danger"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!selected || !amount || note.trim().length < 3}
            icon={faHandHoldingDollar}
            className="w-full justify-center"
          >
            ลดเครดิต
          </UiButton>

          {/* ผลลัพธ์ล่าสุด */}
          {lastResult && (
            <div className="rounded-xl border border-green-200 dark:border-green-900/40 bg-green-50 dark:bg-green-900/20 p-3 text-xs space-y-1">
              <p className="font-semibold text-green-700 dark:text-green-400">ผลการลดเครดิตล่าสุด ({fmt(lastResult.amount)})</p>
              <div className="flex justify-between"><span className="text-gray-600 dark:text-slate-300">หักจาก Main</span><span className="tabular-nums">{fmt(lastResult.from_main)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600 dark:text-slate-300">หักจาก Payout</span><span className="tabular-nums">{fmt(lastResult.from_payout)}</span></div>
              {lastResult.to_main_negative > 0 && (
                <div className="flex justify-between text-red-600 dark:text-red-400"><span>ดัน Main ติดลบ</span><span className="tabular-nums">{fmt(lastResult.to_main_negative)}</span></div>
              )}
              <div className="flex justify-between"><span className="text-gray-600 dark:text-slate-300">Main คงเหลือ</span><span className={`tabular-nums font-bold ${balanceColor(lastResult.balance)}`}>{fmt(lastResult.balance)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600 dark:text-slate-300">Payout คงเหลือ</span><span className={`tabular-nums font-bold ${balanceColor(lastResult.payout_balance)}`}>{fmt(lastResult.payout_balance)}</span></div>
            </div>
          )}
        </div>

        {/* ── ประวัติการลดเครดิต ── */}
        <div className="rounded-2xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
          <p className="font-semibold text-sm text-gray-700 dark:text-slate-200 mb-3">
            <FontAwesomeIcon icon={faClockRotateLeft} className="mr-1.5 text-gray-400" />
            ประวัติการลดเครดิต {selected ? `· ${selected.name}` : ''}
          </p>
          {!selected ? (
            <div className="text-center py-10 text-gray-400 dark:text-slate-500 text-sm">เลือก Merchant เพื่อดูประวัติ</div>
          ) : historyLoading ? (
            <div className="text-center py-10 text-gray-400 dark:text-slate-500 text-sm">กำลังโหลด...</div>
          ) : history.length === 0 ? (
            <div className="text-center py-10 text-gray-400 dark:text-slate-500 text-sm">ยังไม่มีประวัติ</div>
          ) : (
            <div className="space-y-2 max-h-[520px] overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} className="rounded-xl border border-gray-100 dark:border-slate-700 p-3 text-xs">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-bold text-red-600 dark:text-red-400 tabular-nums">− {fmt(h.amount)}</span>
                    <span className="text-gray-400 dark:text-slate-500">
                      {new Date(h.created_at).toLocaleString('th-TH', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-gray-700 dark:text-slate-300 mb-1 break-words">{h.note}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500 dark:text-slate-400 tabular-nums">
                    <span>Main −{fmt(h.from_main)}</span>
                    <span>Payout −{fmt(h.from_payout)}</span>
                    {Number(h.to_main_negative) > 0 && <span className="text-red-500">ติดลบ {fmt(h.to_main_negative)}</span>}
                  </div>
                  <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">
                    โดย {h.admin_name || 'admin'} · Main {fmt(h.balance_before)} → {fmt(h.balance_after)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ReduceCreditPage;
